// System Owner Users Management Page
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { ArrowLeft, Plus, MoreVertical, Trash2, Edit, UserCog, Crown } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useConfirm } from '../../components/common/ConfirmDialog';

const SystemUsersPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [userForm, setUserForm] = useState({ email: '', password: '', first_name: '', last_name: '' });

  const { data: systemOwners = [], isLoading: loading } = useQuery({
    queryKey: QK.systemUsers(),
    queryFn: () => api.get('/system-users').then(r => r.data),
    onError: () => toast.error('Failed to load system owners'),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: QK.systemUsers() });

  const handleCreate = () => {
    setUserForm({
      email: '',
      password: '',
      first_name: '',
      last_name: ''
    });
    setCreateDialogOpen(true);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setUserForm({
      email: user.email,
      password: '',
      first_name: user.first_name,
      last_name: user.last_name
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!userForm.email || !userForm.first_name || !userForm.last_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (createDialogOpen && !userForm.password) {
      toast.error('Password is required for new users');
      return;
    }

    setSaving(true);
    try {
      if (createDialogOpen) {
        // Create new system owner
        await api.post('/system-users', userForm);
        toast.success('System owner created successfully');
      } else {
        // Update existing system owner
        const updateData = {
          first_name: userForm.first_name,
          last_name: userForm.last_name,
          email: userForm.email,
        };
        if (userForm.password) {
          updateData.password = userForm.password;
        }
        
        await api.put(`/system-users/${selectedUser.id}`, updateData);
        toast.success('System owner updated successfully');
      }

      setCreateDialogOpen(false);
      setEditDialogOpen(false);
      invalidate();
    } catch (error) {
      console.error('Error saving system owner:', error);
      toast.error(error.response?.data?.detail || 'Failed to save system owner');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (userId) => {
    if (!await confirm({ title: 'Deactivate System Owner', description: 'Are you sure you want to deactivate this system owner?', destructive: true })) return;

    try {
      await api.post(`/system-users/${userId}/deactivate`);
      toast.success('System owner deactivated');
      invalidate();
    } catch (error) {
      console.error('Error deactivating:', error);
      toast.error(error.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const handleActivate = async (userId) => {
    try {
      await api.post(`/system-users/${userId}/activate`);
      toast.success('System owner activated');
      invalidate();
    } catch (error) {
      console.error('Error activating:', error);
      toast.error(error.response?.data?.detail || 'Failed to activate');
    }
  };

  const handleDelete = async (userId) => {
    if (!await confirm({ title: 'Permanently Delete System Owner', description: 'This action cannot be undone.', destructive: true })) return;

    try {
      await api.delete(`/system-users/${userId}`);
      toast.success('System owner deleted');
      invalidate();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleTransferMain = async (userId) => {
    if (!await confirm({ title: 'Transfer Main Ownership', description: 'Are you sure? You will lose main owner privileges.', destructive: true })) return;

    try {
      const response = await api.post(`/system-users/${userId}/transfer-main`);
      toast.success(response.data.message);
      
      // Show warning that user needs to re-login
      toast.info('Please log out and log back in for changes to take effect');
      
      invalidate();
    } catch (error) {
      console.error('Error transferring main status:', error);
      toast.error(error.response?.data?.detail || 'Failed to transfer main status');
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-primary text-white px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/admin/system')}
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-serif text-xl font-semibold">System Owner Users</h1>
            <p className="text-xs text-white/70">Manage administrators with full system access</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>System Owners</CardTitle>
            <Button onClick={handleCreate} className="bg-green-700 hover:bg-green-800">
              <Plus className="w-4 h-4 mr-2" />
              Add System Owner
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : systemOwners.length === 0 ? (
              <p className="text-center py-12 text-muted-foreground">No system owners found</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {systemOwners.map((owner) => (
                    <TableRow key={owner.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {owner.first_name} {owner.last_name}
                          {owner.is_main_system_owner && (
                            <Crown className="w-4 h-4 text-yellow-500" title="Main System Owner" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{owner.email}</TableCell>
                      <TableCell>
                        <Badge variant={owner.is_active ? 'default' : 'secondary'}>
                          {owner.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {owner.created_at ? new Date(owner.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {owner.last_login ? formatDateTime(owner.last_login) : 'Never'}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(owner)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            
                            {!owner.is_main_system_owner && (
                              <>
                                {owner.is_active ? (
                                  <DropdownMenuItem onClick={() => handleDeactivate(owner.id)}>
                                    <UserCog className="w-4 h-4 mr-2" />
                                    Deactivate
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => handleActivate(owner.id)}>
                                    <UserCog className="w-4 h-4 mr-2" />
                                    Activate
                                  </DropdownMenuItem>
                                )}
                                
                                <DropdownMenuItem 
                                  onClick={() => handleTransferMain(owner.id)}
                                  disabled={!owner.is_active}
                                >
                                  <Crown className="w-4 h-4 mr-2" />
                                  Make Main Owner
                                </DropdownMenuItem>
                                
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(owner.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={createDialogOpen || editDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setCreateDialogOpen(false);
          setEditDialogOpen(false);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {createDialogOpen ? 'Create System Owner' : 'Edit System Owner'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">First Name *</label>
                <Input
                  value={userForm.first_name}
                  onChange={(e) => setUserForm({...userForm, first_name: e.target.value})}
                  placeholder="First name"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Last Name *</label>
                <Input
                  value={userForm.last_name}
                  onChange={(e) => setUserForm({...userForm, last_name: e.target.value})}
                  placeholder="Last name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Email *</label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                placeholder="email@example.com"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Password {createDialogOpen ? '*' : '(leave blank to keep current)'}
              </label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                placeholder={createDialogOpen ? 'Password' : 'Leave blank to keep current'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setCreateDialogOpen(false);
              setEditDialogOpen(false);
            }}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SystemUsersPage;
